import { useEffect, useRef, useState } from "react";

/* Tracks whether the expanded preview has scrolled out of view.
Observation only, so the collapsed bar can never affect layout height.
topOffset excludes the area covered by the fixed header and navbar, which is
never visible even though the viewport extends behind it. */
export const useIsScrolledPast = (ref, topOffset = 0) => {
  const [isScrolledPast, setIsScrolledPast] = useState(false);

  useEffect(() => {
    const element = ref.current;

    if (!element) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setIsScrolledPast(!entry.isIntersecting),
      { threshold: 0, rootMargin: `-${topOffset}px 0px 0px 0px` }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [ref, topOffset]);

  return isScrolledPast;
};

/* Signals only when the rendered result actually differs, so options that the
model cannot depict do not trigger a change that the user will not find. */
export const useHasRenderChanged = (renderKey, isActive) => {
  const [hasChanged, setHasChanged] = useState(false);
  const previousKey = useRef(renderKey);

  useEffect(() => {
    if (previousKey.current === renderKey) {
      return;
    }

    previousKey.current = renderKey;

    if (isActive) {
      setHasChanged(true);
    }
  }, [renderKey, isActive]);

  return [hasChanged, () => setHasChanged(false)];
};
